import React, {
  type ComponentProps,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { DatePicker, Flex, Tooltip, Typography } from 'antd';

import Button from 'components/Button';

import DateTime from 'utils/datetime';

import { type RangePickerProps } from 'antd/es/date-picker';
import type { Dayjs } from 'dayjs';

import './style.scss';

interface DateRangeFilterProps {
  label?: string;
  onChange: (dates: [string, string]) => void;
  rangePresets?: { label: string; value: [Dayjs, Dayjs] }[];
  accessibilityLabel?: string;
}

const now = DateTime.now();

const defaultRangePresets = [
  {
    label: 'Last 7 Days',
    value: [DateTime.add(now, -7, 'd'), now],
  },
  {
    label: 'Last 14 Days',
    value: [DateTime.add(now, -14, 'd'), now],
  },
  { label: 'Last 30 Days', value: [DateTime.add(now, -30, 'd'), now] },
  { label: 'Last 90 Days', value: [DateTime.add(now, -90, 'd'), now] },
];

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  label,
  onChange,
  rangePresets,
  accessibilityLabel,
}) => {
  return (
    <Flex vertical className="custom-date-range-picker">
      <div className="custom-select-container__label">
        {label ?? 'Date Range'}
      </div>
      <DatePicker.RangePicker
        aria-label={accessibilityLabel}
        presets={
          (rangePresets as unknown as RangePickerProps['presets']) ||
          (defaultRangePresets as unknown as RangePickerProps['presets'])
        }
        onChange={(_, dateStrings) => {
          const isoDates = dateStrings?.map((date) =>
            date?.trim()?.length > 0
              ? DateTime.startOfDay(date)?.format()
              : null,
          );
          onChange(isoDates as [string, string]);
        }}
        size="middle"
        style={{ height: '35px' }}
        format="MMM DD, YYYY"
      />
    </Flex>
  );
};

export default DateRangeFilter;

interface WeeklyDateRangePickerProps
  extends ComponentProps<typeof DatePicker.RangePicker> {
  noOfWeeksForward?: number;
  noOfWeeksBackward?: number;
  trackerLabel?: string;
  rootClassName?: string;
  rootStyle?: React.CSSProperties;
}

/**Generates a label for the week based on the selected date range.
 * It checks if the selected dates correspond to the current week, last week, or next week.
 * @param {WeeklyDateRangePickerProps['value']} dates - The selected date range.
 * @returns {string} - A label indicating the week, or an empty string if the dates are not valid.
 */
const getWeekLabel = (dates: WeeklyDateRangePickerProps['value']) => {
  if (!dates || dates.length !== 2) {
    return '';
  }

  const [start, end] = dates as [Dayjs, Dayjs];
  const today = DateTime.now();

  const thisWeekStart = DateTime.startOfWeek(today);

  if (
    DateTime.isSameDay(start, end) &&
    DateTime.isSameDay(start, DateTime.now())
  ) {
    return `Today`;
  }

  if (
    DateTime.isSameWeek(start, end) &&
    DateTime.isSameWeek(start, thisWeekStart)
  ) {
    return `This Week`;
  }

  const oneWeekAgo = DateTime.subtract(today, 7, 'd');
  const lastWeekStart = DateTime.startOfWeek(oneWeekAgo);

  const lastWeekEnd = DateTime.endOfWeek(oneWeekAgo);

  if (
    DateTime.isSameWeek(start, lastWeekStart) &&
    DateTime.isSameWeek(end, lastWeekEnd)
  ) {
    return `Last Week`;
  }

  const nextWeekStart = DateTime.startOfWeek(today.add(7, 'd'));
  const nextWeekEnd = DateTime.endOfWeek(today.add(7, 'd'));

  if (
    DateTime.isSameWeek(start, nextWeekStart) &&
    DateTime.isSameWeek(end, nextWeekEnd)
  ) {
    return `Next Week`;
  }
};

export type RangePickerValue = Parameters<
  NonNullable<ComponentProps<typeof DatePicker.RangePicker>['onChange']>
>[0];

/**
 * This component allows users to select a week-based date range.
 *
 * **Props:**
 * @param {(dates: RangePickerValue, isoDates: [string, string]) => void} onChange - Callback when the date range changes.
 * @param {RangePickerValue} value - The currently selected date range.
 * @param {number} [noOfWeeksForward=2] - Number of weeks forward the user can navigate.
 * @param {number} [noOfWeeksBackward=2] - Number of weeks backward the user can navigate.
 * @param {string} [trackerLabel] - Optional label for tracking analytics.
 * @param {...any} ...props - Additional props passed to the underlying RangePicker.
 *
 */
export const WeeklyDateRangePicker: React.FC<WeeklyDateRangePickerProps> = ({
  onChange,
  value,
  noOfWeeksForward = 2,
  noOfWeeksBackward = 2,
  trackerLabel,
  ...props
}) => {
  // Internal state for uncontrolled usage
  const [internalDates, setInternalDates] = useState<RangePickerValue>();

  const dates = value ?? internalDates;

  const label = useMemo(() => getWeekLabel(dates), [dates]);
  const hasLabel = !!label;

  const weekNavigationRules = useMemo(() => {
    if (!dates?.[0]) {
      return { canNavigateBackwards: false, canNavigateForwards: false };
    }

    const currentWeekStart = DateTime.startOfWeek(dates[0] as Dayjs);
    const now = DateTime.now();

    const earliestAllowed = DateTime.startOfWeek(
      DateTime.subtract(now, noOfWeeksBackward, 'weeks'),
    );

    const latestAllowed = DateTime.startOfWeek(
      DateTime.add(now, noOfWeeksForward, 'weeks'),
    );

    return {
      canNavigateBackwards: DateTime.isAfter(currentWeekStart, earliestAllowed),
      canNavigateForwards: DateTime.isBefore(currentWeekStart, latestAllowed),
    };
  }, [dates?.[0], noOfWeeksBackward, noOfWeeksForward]);

  const { canNavigateBackwards, canNavigateForwards } = weekNavigationRules;

  const handleChange = (nextDates: RangePickerValue) => {
    if (value === undefined) setInternalDates(nextDates);
    onChange?.(
      nextDates,
      nextDates?.map((date) => date?.toISOString()) as [string, string],
    );
  };

  const handleArrowClick = (direction: 'prev' | 'next') => {
    const startOfPrevWeek = DateTime.startOfWeek(
      DateTime.subtract(dates?.[0] as Dayjs, 1, 'week'),
    );
    const endOfPrevWeek = DateTime.endOfWeek(startOfPrevWeek);

    const startOfNextWeek = DateTime.startOfWeek(
      DateTime.add(dates?.[1] as Dayjs, 1, 'week'),
    );
    const endOfNextWeek = DateTime.endOfWeek(startOfNextWeek);

    const datesToSet = (
      direction === 'prev'
        ? [startOfPrevWeek, endOfPrevWeek]
        : [startOfNextWeek, endOfNextWeek]
    ) as RangePickerValue;

    handleChange(datesToSet);
  };

  // Initialize to current week if no value/defaultValue provided
  useEffect(() => {
    if (!dates?.[0] || !dates?.[1]) {
      const start = DateTime.startOfWeek(DateTime.now());
      const end = DateTime.endOfWeek(DateTime.now());
      const initial = [start, end] as RangePickerValue;

      if (value === undefined) setInternalDates(initial);

      onChange?.(initial, [start.toISOString(), end.toISOString()]);
    }
  }, []);

  return (
    <Flex
      className={`weekly-date-range-picker-root ${
        hasLabel ? 'weekly-date-range-picker-root--label' : ''
      } ${props.rootClassName ?? ''}`}
      align="center"
      justify="center"
      gap={10}
      style={props.rootStyle}
    >
      <Tooltip title="Previous Week">
        <Button
          type="default"
          onClick={() => canNavigateBackwards && handleArrowClick('prev')}
          disabled={!canNavigateBackwards}
          className="weekly-date-range-picker__arrow"
          trackerLabel={
            trackerLabel ? `${trackerLabel} - Previous Week` : undefined
          }
        >
          <ArrowLeftOutlined />
        </Button>
      </Tooltip>
      <Flex
        vertical
        className="weekly-date-range-picker"
        align="center"
        justify="center"
      >
        <Typography.Text strong className="m-0">
          {label || ''}
        </Typography.Text>
        <DatePicker.RangePicker
          // open={false}
          allowClear={false}
          value={dates}
          onChange={(d) => handleChange(d as RangePickerValue)}
          suffixIcon={null}
          format={'MMM DD, YYYY'}
          className={
            `weekly-date-range-picker__input ` +
            (hasLabel ? 'weekly-date-range-picker__input--label' : '')
          }
          inputReadOnly
          style={{
            marginTop: hasLabel ? -2 : 0,
          }}
          {...props}
        />
      </Flex>
      <Tooltip title="Next Week">
        <Button
          type="default"
          className="weekly-date-range-picker__arrow"
          onClick={() => canNavigateForwards && handleArrowClick('next')}
          disabled={!canNavigateForwards}
          trackerLabel={
            trackerLabel ? `${trackerLabel} - Next Week` : undefined
          }
        >
          <ArrowRightOutlined />
        </Button>
      </Tooltip>
    </Flex>
  );
};
